import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';

const TEST_TYPES = {
  'typing-speed': 'Typing Speed Test',
  'speed-reading': 'Speed Reading Test',
  'memory-recall': 'Memory Recall Test',
  'typing-accuracy': 'Typing Accuracy Drill',
  'flash-comprehension': 'Flash Comprehension Check'
};

function CognitionLabManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    title: '',
    content: '',
    difficulty: 'medium',
    timeLimit: 300, // 5 minutes default
    passingScore: 70,
    instructions: '',
    story: '', // For speed reading
    text: '', // For flash comprehension
    questions: [] // Array of question objects for both
  });

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const testsRef = collection(db, 'cognitionLabCertificationTests');
      const q = query(testsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const testsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTests(testsData);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const testData = {
        ...formData,
        createdAt: new Date().toISOString(),
        createdBy: user.uid
      };

      // Clean up data based on test type before saving
      if (testData.type === 'speed-reading') {
        delete testData.text;
        delete testData.content;
        delete testData.instructions; // Instructions not currently used for speed/flash based on user components
        testData.questions = (testData.questions || []).map(q => ({
          q: q.q || '',
          a: q.a || '',
          options: q.options || [] // Ensure options is an array
        })).filter(q => q.q); // Filter out questions with no text
      } else if (testData.type === 'flash-comprehension') {
        delete testData.story;
        delete testData.content;
        delete testData.instructions; // Instructions not currently used for speed/flash based on user components
         testData.questions = (testData.questions || []).map(q => ({
          q: q.q || '',
          a: q.a || '',
          options: q.options || []
        })).filter(q => q.q); // Filter out questions with no text
      } else {
        // For other types, ensure story, text, and questions are removed
        delete testData.story;
        delete testData.text;
        delete testData.questions;
         // Keep instructions and content for other types
      }

      await addDoc(collection(db, 'cognitionLabCertificationTests'), testData);
      setShowAddModal(false);
      setFormData({
        type: '',
        title: '',
        content: '',
        difficulty: 'medium',
        timeLimit: 300,
        passingScore: 70,
        instructions: '',
        story: '',
        text: '',
        questions: []
      });
      fetchTests();
    } catch (error) {
      console.error('Error adding test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'cognitionLabCertificationTests', testId));
        fetchTests();
      } catch (error) {
        console.error('Error deleting test:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateTest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updatedData = {
        ...formData,
        updatedAt: new Date().toISOString(),
        updatedBy: user.uid
      };

      // Clean up data based on test type before saving
       if (updatedData.type === 'speed-reading') {
        delete updatedData.text;
        delete updatedData.content;
         delete updatedData.instructions; // Instructions not currently used for speed/flash based on user components
        updatedData.questions = (updatedData.questions || []).map(q => ({
          q: q.q || '',
          a: q.a || '',
          options: updatedData.questions[updatedData.questions.indexOf(q)]?.options || [] // Ensure options is an array, handle potential index issues
        })).filter(q => q.q); // Filter out questions with no text
      } else if (updatedData.type === 'flash-comprehension') {
        delete updatedData.story;
        delete updatedData.content;
         delete updatedData.instructions; // Instructions not currently used for speed/flash based on user components
         updatedData.questions = (updatedData.questions || []).map(q => ({
          q: q.q || '',
          a: q.a || '',
          options: updatedData.questions[updatedData.questions.indexOf(q)]?.options || []
        })).filter(q => q.q); // Filter out questions with no text
      } else {
        // For other types, ensure story, text, and questions are removed
        delete updatedData.story;
        delete updatedData.text;
        delete updatedData.questions;
        // Keep instructions and content for other types
      }

      await updateDoc(doc(db, 'cognitionLabCertificationTests', editingTest.id), updatedData);
      setEditingTest(null);
      setFormData({
        type: '',
        title: '',
        content: '',
        difficulty: 'medium',
        timeLimit: 300,
        passingScore: 70,
        instructions: '',
        story: '',
        text: '',
        questions: []
      });
      fetchTests();
    } catch (error) {
      console.error('Error updating test:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Cognition Lab Test Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[rgb(130,88,18)] text-white px-4 py-2 rounded-lg hover:bg-[rgb(110,68,0)] transition-colors"
        >
          Add New Test
        </button>
      </div>

      {/* Test List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tests.map((test) => (
          <div key={test.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{test.title}</h3>
                <p className="text-sm text-gray-600">{TEST_TYPES[test.type]}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                test.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                test.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {test.difficulty}
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Time Limit:</span> {test.timeLimit / 60} minutes
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Passing Score:</span> {test.passingScore}%
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setEditingTest(test);
                  setFormData(test);
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteTest(test.id)}
                className="text-red-600 hover:text-red-800"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingTest) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTest ? 'Edit Test' : 'Add New Test'}
            </h2>
            
            <form onSubmit={editingTest ? handleUpdateTest : handleAddTest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="">Select a test type</option>
                  {Object.entries(TEST_TYPES).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              {/* Conditional fields based on test type */}
              {formData.type === 'speed-reading' && (
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Passage Story
                  </label>
                  <textarea
                    value={formData.story}
                    onChange={(e) => setFormData({...formData, story: e.target.value})}
                    className="w-full p-2 border rounded-md h-48"
                    required
                  />
                </div>
              )}

               {formData.type === 'flash-comprehension' && (
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Passage Text
                  </label>
                  <textarea
                    value={formData.text}
                    onChange={(e) => setFormData({...formData, text: e.target.value})}
                    className="w-full p-2 border rounded-md h-48"
                    required
                  />
                </div>
              )}

              {(formData.type === 'speed-reading' || formData.type === 'flash-comprehension') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Questions
                    </label>
                    {(formData.questions || []).map((q, qIndex) => (
                      <div key={qIndex} className="border rounded-md p-4 mb-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                           <h4 className="font-semibold">Question {qIndex + 1}</h4>
                           <button
                             type="button"
                             onClick={() => setFormData({...formData, questions: formData.questions.filter((_, i) => i !== qIndex)})}
                             className="text-red-600 hover:text-red-800 text-sm"
                           >
                             Remove Question
                           </button>
                        </div>
                        
                        <div className="mb-2">
                           <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                           <input
                             type="text"
                             value={q.q}
                             onChange={(e) => {
                               const newQuestions = [...(formData.questions || [])];
                               newQuestions[qIndex].q = e.target.value;
                               setFormData({...formData, questions: newQuestions});
                             }}
                             className="w-full p-2 border rounded-md"
                             required
                           />
                        </div>

                         {/* Assuming multiple choice for now, can add options for other types later */}
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma-separated)</label>
                           <input
                             type="text"
                             value={(q.options || []).join(',')}
                             onChange={(e) => {
                               const newQuestions = [...(formData.questions || [])];
                               newQuestions[qIndex].options = e.target.value.split(',').map(opt => opt.trim());
                               setFormData({...formData, questions: newQuestions});
                             }}
                             className="w-full p-2 border rounded-md"
                             required
                           />
                         </div>

                         <div className="mt-2">
                           <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer (must match one of the options)</label>
                           <input
                             type="text"
                             value={q.a}
                             onChange={(e) => {
                               const newQuestions = [...(formData.questions || [])];
                               newQuestions[qIndex].a = e.target.value;
                               setFormData({...formData, questions: newQuestions});
                             }}
                             className="w-full p-2 border rounded-md"
                             required
                           />
                         </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => setFormData({...formData, questions: [...(formData.questions || []), {q: '', a: '', options: []}]})}
                      className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Add Question
                    </button>
                  </div>
              )}

              {/* Keep content and instructions for other test types */}
              {formData.type !== 'speed-reading' && formData.type !== 'flash-comprehension' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      className="w-full p-2 border rounded-md h-32"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instructions
                    </label>
                    <textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                      className="w-full p-2 border rounded-md h-24"
                      required
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                    className="w-full p-2 border rounded-md"
                    required
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Limit (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.timeLimit}
                    onChange={(e) => setFormData({...formData, timeLimit: parseInt(e.target.value)})}
                    className="w-full p-2 border rounded-md"
                    required
                    min="60"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Passing Score (%)
                  </label>
                  <input
                    type="number"
                    value={formData.passingScore}
                    onChange={(e) => setFormData({...formData, passingScore: parseInt(e.target.value)})}
                    className="w-full p-2 border rounded-md"
                    required
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingTest(null);
                    setFormData({
                      type: '',
                      title: '',
                      content: '',
                      difficulty: 'medium',
                      timeLimit: 300,
                      passingScore: 70,
                      instructions: '',
                      story: '',
                      text: '',
                      questions: []
                    });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-lg hover:bg-[rgb(110,68,0)] transition-colors"
                >
                  {editingTest ? 'Update Test' : 'Add Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CognitionLabManagement; 